import { forwardRef, type RefObject, useMemo } from 'react'
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList
} from '@/components/ui/combobox'
import { XCloseIcon } from '@/components/ui/icons'
import type { ComboboxOption } from '@/features/marquee/components/combobox-field'
import { ComboboxTrigger } from '@/features/marquee/components/combobox-trigger'

interface ManagerMultiSelectProps {
	options: ComboboxOption[]
	/** Selected manager ids. */
	value: string[]
	onChange: (value: string[]) => void
	placeholder: string
	searchPlaceholder: string
	emptyLabel: string
	/** aria-label for a pill's remove button, given the manager's name. */
	removeLabel: (name: string) => string
	id?: string
	hasError?: boolean
	/** Show a spinner in place of the chevron while options load (implies disabled). */
	loading?: boolean
	/**
	 * Where to portal the option list. Pass the surrounding modal's content
	 * element when this renders inside a drawer or dialog: those disable pointer
	 * events outside their own subtree, so a list left on the body cannot be
	 * scrolled or tapped, and interacting with it dismisses the modal. Defaults
	 * to the body.
	 */
	popupContainer?: RefObject<HTMLElement | null>
}

/**
 * Multi-select for market managers: a select-style combobox trigger that always
 * shows the placeholder, opens a searchable checklist, and renders the current
 * selection as removable pills below the field.
 */
export const ManagerMultiSelect = forwardRef<
	HTMLDivElement,
	ManagerMultiSelectProps
>(function ManagerMultiSelect(
	{
		options,
		value,
		onChange,
		placeholder,
		searchPlaceholder,
		emptyLabel,
		removeLabel,
		id,
		hasError,
		loading,
		popupContainer
	},
	ref
) {
	// base-ui matches `{ value, label }` objects referentially, so derive the
	// selection from `options` to keep the same references the list renders.
	const selectedOptions = useMemo(
		() => options.filter((option) => value.includes(option.value)),
		[options, value]
	)

	const removeManager = (managerValue: string) =>
		onChange(value.filter((id) => id !== managerValue))

	return (
		<div className='flex flex-col gap-3'>
			<Combobox
				disabled={loading}
				items={options}
				multiple
				onValueChange={(next) => onChange(next.map((o) => o.value))}
				value={selectedOptions}
			>
				<ComboboxTrigger
					disabled={loading}
					hasError={hasError}
					id={id}
					loading={loading}
					ref={ref}
				>
					<span className='truncate text-neutral-400'>
						{placeholder}
					</span>
				</ComboboxTrigger>
				{/* Open upward so the selected chips below the field stay visible
				    while picking. */}
				<ComboboxContent
					className='w-(--anchor-width)'
					container={popupContainer}
					side='top'
				>
					<ComboboxInput placeholder={searchPlaceholder} />
					<ComboboxList>
						{(item: ComboboxOption) => (
							<ComboboxItem
								className='whitespace-nowrap'
								key={item.value}
								value={item}
							>
								{item.label}
							</ComboboxItem>
						)}
					</ComboboxList>
					<ComboboxEmpty>{emptyLabel}</ComboboxEmpty>
				</ComboboxContent>
			</Combobox>

			{selectedOptions.length > 0 && (
				<div className='flex flex-wrap items-center gap-2'>
					{selectedOptions.map((option) => (
						<span
							className='flex items-center gap-2 rounded-[32px] border-[1.5px] border-grey-1000 px-3 py-2 text-grey-1000 text-xs'
							key={option.value}
						>
							{option.label}
							<button
								aria-label={removeLabel(option.label)}
								className='flex cursor-pointer items-center text-grey-1000'
								onClick={() => removeManager(option.value)}
								type='button'
							>
								<XCloseIcon size={16} />
							</button>
						</span>
					))}
				</div>
			)}
		</div>
	)
})

ManagerMultiSelect.displayName = 'ManagerMultiSelect'
